import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, TrendingUp, TrendingDown, Lightbulb, ArrowRight, BookOpen, Mail, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/app/Common";
import { SimpleSelect } from "@/components/app/SimpleSelect";
import { Helpful } from "@/components/app/Playbook";
import { AREAS } from "@/components/app/GrowthDialogs";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

const area = (k) => AREAS.find((a) => a[0] === k)?.[1] || k;
const FMT = { money: (v) => `$${Math.round(v).toLocaleString()}`, time: (v) => `${Math.round(v).toLocaleString()} h`, people: (v) => `${Math.round(v * 10) / 10}` };
const RES = [["money", "Capital"], ["time", "Owner & team time"], ["people", "People (headcount-months)"]];

const Split = ({ data, fmt, fn }) => {
  const rows = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = rows.reduce((s, r) => s + r[1], 0) || 1;
  return rows.length ? <ul className="space-y-1.5 text-xs">{rows.map(([k, v]) => <li key={k}><div className="truncate" title={fn ? area(k) : k}>{fn ? area(k) : k}</div><div className="font-mono text-muted-foreground">{fmt(v)} · {Math.round((v / total) * 100)}%</div></li>)}</ul>
    : <p className="text-xs text-muted-foreground">None</p>;
};

const Mix = ({ mix }) => (
  <div className="grid gap-4 md:grid-cols-3" data-testid="qr-mix">
    {RES.map(([k, l]) => (
      <div key={k} className="surface p-4" data-testid={`qr-mix-${k}`}>
        <div className="flex justify-between items-baseline"><span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{l}</span><b className="font-mono">{FMT[k](mix[k].total)}</b></div>
        <div className="grid grid-cols-2 gap-3 mt-3"><div><div className="text-[11px] text-muted-foreground mb-1">By business</div><Split data={mix[k].by_business} fmt={FMT[k]} /></div>
          <div><div className="text-[11px] text-muted-foreground mb-1">By function</div><Split data={mix[k].by_function} fmt={FMT[k]} fn /></div></div>
      </div>))}
  </div>
);

const List = ({ title, icon: Icon, color, items, testId }) => (items?.length ? (
  <div className="surface p-5" data-testid={testId}><div className="flex items-center gap-2 font-semibold text-sm mb-2"><Icon className={`h-4 w-4 ${color}`} />{title}</div>
    <ul className="space-y-2 text-sm">{items.map((x) => <li key={typeof x === "string" ? x : x.title}>{typeof x === "string" ? x : <><b>{x.title}</b> <span className="text-muted-foreground">— {x.why}</span></>}</li>)}</ul></div>) : null);

const Review = ({ r, reload }) => {
  const [busy, setBusy] = useState(false);
  const email = async () => {
    setBusy(true);
    try {
      toast.success(`Review sent to ${(await api.post(`/growth/quarterly-reviews/${r.quarter}/email`)).data.to}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid={`qr-review-${r.quarter}`}>
      <div className="surface p-6 border-l-4 border-primary/50">
        <div className="flex flex-wrap items-start justify-between gap-3"><div className="max-w-3xl">
          <div className="text-xs text-muted-foreground">{r.quarter} · {fmtDate(r.start)} – {fmtDate(r.end)} · generated {fmtDate(r.created_at)}</div>
          <h2 className="text-lg font-semibold mt-1" data-testid="qr-headline">{r.headline}</h2><p className="text-sm text-muted-foreground mt-1">{r.summary}</p></div>
          <Button size="sm" variant="outline" onClick={email} disabled={busy} data-testid="qr-email-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}Email me</Button></div>
      </div>
      <Mix mix={r.mix} />
      {Object.keys(r.roi_by_function || {}).length > 0 && <div className="surface p-5" data-testid="qr-roi-function"><div className="font-semibold text-sm mb-2">Return on capital by function</div>
        <div className="flex flex-wrap gap-4 text-sm">{Object.entries(r.roi_by_function).map(([k, v]) => <span key={k}>{area(k)} <b className="font-mono">{v.roi_pct ?? "—"}%</b></span>)}</div></div>}
      <div className="grid gap-4 md:grid-cols-2">
        <List title="Exceeded expectations" icon={TrendingUp} color="text-emerald-500" items={r.exceeded_ai?.length ? r.exceeded_ai : r.exceeded.map((x) => ({ title: x.title, why: x.note || x.rating }))} testId="qr-exceeded" />
        <List title="Underperformed forecasts" icon={TrendingDown} color="text-red-500" items={r.underperformed_ai?.length ? r.underperformed_ai : r.underperformed.map((x) => ({ title: x.title, why: x.note || x.rating }))} testId="qr-underperformed" />
        <List title="What drove success" icon={CheckCircle2} color="text-emerald-500" items={r.drivers_success} testId="qr-drivers-success" />
        <List title="What drove misses" icon={XCircle} color="text-red-500" items={r.drivers_failure} testId="qr-drivers-failure" />
      </div>
      <List title="Recommended changes for next quarter" icon={ArrowRight} color="text-primary" items={r.recommendations} testId="qr-recommendations" />
      {r.insights?.length > 0 && <div className="surface p-5" data-testid="qr-insights"><div className="flex items-center gap-2 font-semibold text-sm mb-2"><BookOpen className="h-4 w-4 text-primary" />New insights · {r.added_to_playbook} added to your Decision Playbook</div>
        <ul className="space-y-2 text-sm">{r.insights.map((i) => <li key={i.title} className="flex gap-2"><Lightbulb className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /><span><b>{i.title}</b> <span className="text-muted-foreground">— {i.guidance}</span> <span className="text-[11px] text-muted-foreground">({i.confidence} confidence)</span></span></li>)}</ul></div>}
      <Helpful feature="quarterly_review" itemId={r.quarter} />
    </div>
  );
};

export const QuarterlyReview = ({ owner }) => {
  const state = useLoad(() => api.get("/growth/quarterly-reviews").then((r) => r.data));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  if (state.loading) return <div className="surface p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (state.error) return <p className="text-sm text-muted-foreground" data-testid="qr-error">{state.error}</p>;
  const d = state.data;
  const quarters = [...new Set([d.current, d.previous, ...d.reviews.map((r) => r.quarter)])];
  const sel = q || d.reviews[0]?.quarter || d.previous;
  const review = d.reviews.find((r) => r.quarter === sel);
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/growth/quarterly-reviews", { quarter: sel });
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div data-testid="quarterly-review">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="w-48"><SimpleSelect value={sel} onChange={setQ} options={quarters.map((x) => ({ value: x, label: `${x}${x === d.current ? " (to date)" : ""}${d.reviews.some((r) => r.quarter === x) ? "" : " · not generated"}` }))} testId="qr-quarter-select" /></div>
        {owner && <Button onClick={run} disabled={busy} data-testid="qr-generate-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{review ? "Regenerate" : "Generate review"}</Button>}
        <span className="text-xs text-muted-foreground">Generated and emailed automatically on the 1st of each quarter.</span>
      </div>
      {review ? <Review r={review} reload={state.reload} /> : <Empty icon={Sparkles} title={`No review for ${sel} yet`} text="Claude summarises where your capital, time and people went, what beat or missed forecast, why, and what to change next quarter — then adds the lessons to your playbook." testId="qr-empty" />}
    </div>
  );
};
